import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  SxProps,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import InventoryAPI from 'api/faber/InventoryAPI'
import DownloadIcon from 'assets/icons/circles-design/download_icon.png'
import PlusIcon from 'assets/icons/circles-design/plus.png'
import OrbitSession from 'hooks/useCookies'

import PageLayout from '../../../common/Layout/Page/PageLayout'
import { ColorBoxContent } from '../../../common/ui/colorBox'

import CRUDDataGrid from './components/CRUDDataGrid'

const TEMPLATE_HEADERS = [
  { label: 'iccid', key: 'iccid' },
  { label: 'matching_id', key: 'matching_id' },
  { label: 'sm_dp_address', key: 'sm_dp_address' },
  { label: 'qr_code', key: 'qr_code' }, 
  { label: 'esim_config_id', key: 'esim_config_id' }
]

const TEMPLATE_DATA = [
  {
    iccid: '8910300000003337426',
    matching_id: 'TN20211125163054DACD90C2',
    sm_dp_address: 'consumer.rsp.global',
    qr_code: 'LPA:1$consumer.rsp.global$TN20211125163054DACD90C2',
    esim_config_id: '1'
  }
]

const stepIconSx: SxProps = {
  '.MuiStepIcon-root': {
    fill: 'black',
    fontWeight: 'bold'
  },
  '.Mui-active': {
    fill: 'blue',
    fontWeight: 'bold'
  }
}

export default function UploadESims() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [uploadedBatch, setUploadedBatch] = useState<any>(null)
  const [rows, setRows] = useState<IESimRow[]>([])
  const [activeStep, setActiveStep] = useState(0)
  const [, setError] = useState<boolean>(false)

  const navigate = useNavigate()

  const handleNextStep = () => setActiveStep((prev) => prev + 1)
  const handlePrevStep = () =>
    setActiveStep((prev) => {
      if (prev === 0) {
        navigate('..')
        return prev
      } else {
        return prev - 1
      }
    })
  const faberToken = OrbitSession.get('faberToken')
  const { mutate: uploadTelnaCSV } = useMutation(InventoryAPI.uploadTelnaCSV, {
    onError: () => {
      setError(true)
    }
  })

  const DownloadTemplate = () => {
    return (
      <div id='uploadESIM__downloadTemplateBtn'>
        <CSVLink
          data={TEMPLATE_DATA}
          filename='esim-batch-upload.csv'
          headers={TEMPLATE_HEADERS}
          style={{ textDecoration: 'none' }}
        >
          <Box
            display='flex'
            alignItems='center'
            sx={{ paddingTop: 3, paddingBottom: 3 }}
          >
            <Typography
              sx={{
                color: '#0000FF',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              Download Template
            </Typography>
            <img
              src={DownloadIcon}
              alt='download-template-logo'
              width='20px'
              height='20px'
            />
          </Box>
        </CSVLink>
      </div>
    )
  }

  const handleCSVParse = () => {
    if (!uploadedBatch) {
      return
    }

    const parseCsv = (str): IESimRow[] | undefined => {
      const rowArray = str.split('\n')

      // Parse CSV headers and clean them (convert to lowercase for case-insensitive comparison)
      const csvHeaders = rowArray[0].replaceAll('"', '').split(',').map(h => h.trim().toLowerCase())
      const expectedHeaders = TEMPLATE_HEADERS.map(hdrObj => hdrObj.key.toLowerCase())
      
      // Check if all expected headers are present (order doesn't matter, case-insensitive)
      const missingHeaders = expectedHeaders.filter(expectedHeader => 
        !csvHeaders.includes(expectedHeader)
      )
      
      if (missingHeaders.length > 0) {
        toast.error(`Missing required headers: ${missingHeaders.join(', ')}`)
        return
      }
      
      // Check for extra headers that shouldn't be there (case-insensitive)
      const extraHeaders = csvHeaders.filter(csvHeader => 
        !expectedHeaders.includes(csvHeader)
      )
      
      if (extraHeaders.length > 0) {
        toast.error(`Unexpected headers found: ${extraHeaders.join(', ')}`)
        return
      }

      const parsedArray: IESimRow[] = []

      // Create a mapping of header names to their indices (case-insensitive)
      const headerIndexMap: { [key: string]: number } = {}
      const originalCsvHeaders = rowArray[0].replaceAll('"', '').split(',').map(h => h.trim())
      
      originalCsvHeaders.forEach((originalHeader, index) => {
        const normalizedHeader = originalHeader.toLowerCase()
        headerIndexMap[normalizedHeader] = index
      })

      const headerStrippedArray = rowArray.slice(1)
      headerStrippedArray.forEach((rowStr, index) => {
        const cleanedRowArr = rowStr.replaceAll('"', '').split(',')

        const parsedRowObj = {
          id: index,
          iccid: cleanedRowArr[headerIndexMap['iccid']] || '',
          matching_id: cleanedRowArr[headerIndexMap['matching_id']] || '',
          sm_dp_address: cleanedRowArr[headerIndexMap['sm_dp_address']] || '',
          qr_code: cleanedRowArr[headerIndexMap['qr_code']] || '',
          esim_config_id: cleanedRowArr[headerIndexMap['esim_config_id']] || '',
          isNew: false
        }

        parsedArray.push(parsedRowObj)
      })

      return parsedArray
    }

    setError(false)
    const reader = new FileReader()

    reader.onload = async (e) => {
      if (!e.target) {
        return
      }

      const text = e.target.result
      const parsedText = parseCsv(text)

      if (parsedText) {
        setRows(parsedText)
      }
    }

    reader.readAsText(uploadedBatch)
  }

  const ParseFileContents = () => {
    return (
      <>
        <div id='batchUpdate__uploadFileArea'>
          <Typography sx={{ paddingTop: 3, paddingBottom: 3 }}>
            Upload File:
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ fontSize: '12px' }}>
            ⚠️ Please ensure the script matches the latest schema and contains no more than 15,000 records.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
              border: '1px dashed #B8C4D3',
              borderRadius: '8px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                direction: 'column',
                margin: '50px auto'
              }}
            >
              <label htmlFor='uploadTelnaFile' style={{ cursor: 'pointer' }}>
                <Box display='flex' alignItems='center' justifyContent='center'>
                  <img src={PlusIcon} alt='plus-icon' width='20px' />
                  <Typography
                    variant='h6'
                    style={{
                      color: '#0000FF',
                      marginLeft: 8
                    }}
                  >
                    Select File
                  </Typography>
                </Box>
              </label>
              <input
                id='uploadTelnaFile'
                type='file'
                accept='.csv'
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: any) => {
                  /** Fix Typing */
                  setUploadedBatch(e.target!.files[0])
                }}
                hidden
              />
            </Box>
          </Box>
        </div>
        {uploadedBatch && (
          <Box display='flex'>
            <Typography>Uploaded File:</Typography>
            <Typography variant='h6'>{uploadedBatch.name}</Typography>
          </Box>
        )}

        <Typography variant='subtitle1' style={{ color: '#B8C4D3' }}>
          csv, max 8mb
        </Typography>
      </>
    )
  }

  const handleBatchSubmit = async () => {
    /* TODO: CSV upload */
    const headers = TEMPLATE_HEADERS.map((hdrObj) => {
      return hdrObj.key
    }).join(',')

    const csvData = rows
      .map((row) => {
        if (!row.isNew) {
          return `${row.iccid},${row.matching_id},${row.sm_dp_address},${row.qr_code},${row.esim_config_id}`
        }
      })
      .join('\n')

    const csvString = `${headers}\n${csvData}`

    const formData = new FormData()
    formData.append('file', new Blob([csvString], { type: 'text/csv' }))

    uploadTelnaCSV({ faberToken, payload: formData })
  }

  const steps = [
    {
      label: 'Download Template',
      description:
        'Download and follow this template to upload a list of telna ESIM.',
      component: <DownloadTemplate />,
      handleNextStep
    },
    {
      label: 'Parse File Contents',
      description:
        'Assign the ICCID, Matching ID, Subscription Management Root-Discovery Service Address, QR Code, ESIM Config ID in their respective columns and upload the file here.',
      component: <ParseFileContents />,
      handleNextStep: () => {
        handleCSVParse()
        handleNextStep()
      }
    },
    {
      label: 'Check contents',
      description: 'Please check. (Only shows 10 rows)',
      component: <CRUDDataGrid {...{ rows, setRows }} />,
      handleNextStep: () => {
        handleBatchSubmit()
        handleNextStep()
      }
    }
  ]

  return (
    <PageLayout header='Telna CSV Upload'>
      <Grid container direction='column' spacing={2}>
        <Grid item>
          <ColorBoxContent variant='white'>
            <Stepper
              activeStep={activeStep}
              orientation='vertical'
              sx={stepIconSx}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      index === steps.length - 1 ? (
                        <Typography variant='caption'>Last step</Typography>
                      ) : null
                    }
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                    <Box> {step.component}</Box>
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant='contained'
                          onClick={step.handleNextStep}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          {index === steps.length - 1 ? 'Submit' : 'Continue'}
                        </Button>
                        <Button onClick={handlePrevStep} sx={{ mt: 1, mr: 1 }}>
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === steps.length && (
              <Paper square elevation={0} sx={{ p: 3 }}>
                <Typography>All steps completed!</Typography>
                <Button onClick={() => navigate('..')} sx={{ mt: 1, mr: 1 }}>
                  Back
                </Button>
              </Paper>
            )}
          </ColorBoxContent>
        </Grid>
        {/* <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <DialogTitle>
            <Typography variant='h4'>{error ? 'Error' : 'Success'}</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>{error ? ERR_UNEXPECTED : dialogMsg}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant='primary' onClick={() => setIsDialogOpen(false)}>
              Done
            </Button>
          </DialogActions>
        </Dialog> */}
      </Grid>
    </PageLayout>
  )
}

interface IESimRow {
  id: string
  iccid: string
  matching_id: string
  sm_dp_address: string
  qr_code: string
  esim_config_id: number
  isNew: boolean
}
